import type {
  RiskAssessmentSection,
  RiskAssessmentTemplate,
  RiskAssessmentTemplateStatus
} from '@gcv/shared';
import { injectable } from 'inversify';

import { banksApi } from './api-util/api';

export interface GetRiskAssessmentTemplatesParams {
  status?: RiskAssessmentTemplateStatus;
  /** Which versions to return, e.g. 'draft', 'latest', or 'draft,latest'. Omit for latest published only. */
  version?: string;
  /** When true, return only the most-recently-updated version of each logical template. */
  last_updated_only?: boolean;
  /** When true, include archived templates in the results. */
  include_archived?: boolean;
}

export interface CreateRiskAssessmentTemplatePayload {
  name: string;
  description?: string;
  sections?: RiskAssessmentSection[];
  /** Resolved from the bank's published band sets when omitted; required when it can't be inferred. */
  risk_band_id?: string;
}

export interface UpdateRiskAssessmentTemplatePayload {
  name: string;
  description?: string;
  sections?: RiskAssessmentSection[];
  risk_band_id?: string;
}

/** Whether a delete removed the never-published template or archived a published one. */
export interface DeleteRiskAssessmentTemplateResult {
  outcome: 'deleted' | 'archived';
  versions: string[];
}

@injectable()
export class RiskAssessmentTemplatesApi {
  async getRiskAssessmentTemplatesForBank(
    bankId: string,
    params?: GetRiskAssessmentTemplatesParams
  ): Promise<RiskAssessmentTemplate[]> {
    return await banksApi().get(
      `/banks/${bankId}/risk-assessment-templates`,
      params as Record<string, unknown> | undefined
    );
  }

  async createRiskAssessmentTemplate(
    bankId: string,
    data: CreateRiskAssessmentTemplatePayload
  ): Promise<RiskAssessmentTemplate> {
    return await banksApi().post(`/banks/${bankId}/risk-assessment-templates`, data);
  }

  async updateRiskAssessmentTemplate(
    bankId: string,
    id: string,
    data: UpdateRiskAssessmentTemplatePayload
  ): Promise<RiskAssessmentTemplate> {
    return await banksApi().put(`/banks/${bankId}/risk-assessment-templates/${id}`, data);
  }

  async deleteRiskAssessmentTemplate(
    bankId: string,
    id: string
  ): Promise<DeleteRiskAssessmentTemplateResult> {
    return await banksApi().delete(`/banks/${bankId}/risk-assessment-templates/${id}`);
  }

  async publishRiskAssessmentTemplate(bankId: string, id: string): Promise<RiskAssessmentTemplate> {
    return await banksApi().post(`/banks/${bankId}/risk-assessment-templates/${id}/publish`, {});
  }
}
